/**
 * useOutreachThread — one roster entry's message thread (oldest first) and
 * the three per-message recovery actions.
 *
 * Each action is its own logical request — a click, not a retryable
 * multi-step flow — so the Idempotency-Key is minted inside mutationFn per
 * call, the same convention useOutreachContact follows. TanStack does not
 * retry mutations by default, so no retry ever reuses a key by accident.
 * Cancel takes no key at all: the BE treats a second cancel as the first.
 * Stopping a queued first touch makes the BE write the draft back (E9-53), so
 * a cancel also refreshes the wizard's drafts.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { OutreachEntryEvent, OutreachMessage, OutreachQueuedMessage, OutreachRow } from '@discovr/types';
import { campaignKeys } from '../queryKeys';
import {
  cancelOutreachMessage,
  fetchOutreachEntryEvents,
  fetchOutreachMessage,
  fetchOutreachThread,
  markOutreachMessageSent,
  sendNowOutreachMessage,
} from '../api/outreachApi';

/** The thread drawer shows one page; 2.3 has no infinite scroll yet. */
export const OUTREACH_THREAD_PAGE = 1;
export const OUTREACH_THREAD_LIMIT = 50;

export function useOutreachThread(entryId: string | null) {
  return useQuery<{ rows: OutreachMessage[]; total: number }>({
    queryKey: campaignKeys.outreachThread(entryId ?? ''),
    queryFn: () => fetchOutreachThread(entryId as string, OUTREACH_THREAD_PAGE, OUTREACH_THREAD_LIMIT),
    enabled: !!entryId,
  });
}

/** The admin actions shown as timeline lines in the thread (E9-93). A failed read just leaves them out. */
export function useOutreachEntryEvents(entryId: string | null) {
  return useQuery<OutreachEntryEvent[]>({
    queryKey: campaignKeys.outreachEvents(entryId ?? ''),
    queryFn: () => fetchOutreachEntryEvents(entryId as string),
    enabled: !!entryId,
  });
}

export function useOutreachMessageHTML(entryId: string | null, messageId: number | null) {
  return useQuery<OutreachMessage>({
    queryKey: campaignKeys.outreachMessage(entryId ?? '', messageId ?? 0),
    queryFn: () => fetchOutreachMessage(entryId as string, messageId as number),
    enabled: !!entryId && !!messageId,
    staleTime: 5 * 60_000,
  });
}

/** Which hold the operator chose to send past: a recent contact, or a risky address (with the address they saw). */
export type SendAnywayVars =
  | { messageId: number; override: 'cooldown' }
  | { messageId: number; override: 'address_guard'; expectedTo: string };

export interface MarkSentVars {
  messageId: number;
  reason: string;
}

const PROJECTION_CATCH_UP_MS = 3000;

/** Stop on a first touch writes the draft back, but the row's first-touch fields come off the projection, which
 *  the fact reaches a moment later: an immediate refetch still reads "scheduled" (E9-129). Both cache shapes under
 *  the rows prefix: a table page and the single-row read. */
function withDraftBack(data: unknown, entryId: string): unknown {
  if (!data || typeof data !== 'object') return data;
  const back = (r: OutreachRow): OutreachRow => {
    if (r.entryId !== entryId || !r.firstTouch || (r.firstTouch.status !== 'scheduled' && r.firstTouch.status !== 'queued')) return r;
    const { nextSendAt: _gone, ...rest } = r;
    return { ...rest, firstTouch: { status: 'awaiting_approval' } };
  };
  if ('rows' in data && Array.isArray((data as { rows: OutreachRow[] }).rows)) {
    const page = data as { rows: OutreachRow[] };
    return { ...page, rows: page.rows.map(back) };
  }
  return back(data as OutreachRow);
}

export function useOutreachThreadActions(campaignId: string, entryId: string) {
  const queryClient = useQueryClient();

  // A message action changes the thread AND the row's message column/status
  // pill, which live under different keys.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: campaignKeys.outreachThread(entryId) });
    queryClient.invalidateQueries({ queryKey: campaignKeys.outreachRowsAll(campaignId) });
    queryClient.invalidateQueries({ queryKey: campaignKeys.outreachCounts(campaignId) });
  };

  const resend = useMutation<OutreachQueuedMessage, Error, number>({
    mutationFn: (messageId) => sendNowOutreachMessage(entryId, messageId, crypto.randomUUID()),
    onSuccess: invalidate,
  });

  const sendAnyway = useMutation<OutreachQueuedMessage, Error, SendAnywayVars>({
    mutationFn: (v) =>
      sendNowOutreachMessage(
        entryId,
        v.messageId,
        crypto.randomUUID(),
        v.override === 'address_guard' ? { addressGuard: { expectedTo: v.expectedTo } } : { overrideCooldown: true },
      ),
    // A refusal usually means the message or the creator's email changed: refetch so the thread shows it.
    onSettled: invalidate,
  });

  const markSent = useMutation<OutreachMessage, Error, MarkSentVars>({
    mutationFn: ({ messageId, reason }) => markOutreachMessageSent(entryId, messageId, reason, crypto.randomUUID()),
    onSuccess: invalidate,
  });

  const cancel = useMutation<OutreachMessage, Error, number>({
    mutationFn: (messageId) => cancelOutreachMessage(entryId, messageId),
    onSuccess: (cancelled) => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.outreachDraftsAll(campaignId) });
      // E9-165: Go says whether a draft came back; a first touch that only cancelled leaves the row as it was.
      if (cancelled?.kind === 'initial' && !cancelled.draftReturned) toast.message('Mail stopped. No draft comes back.');
      if (cancelled?.kind === 'initial' && cancelled.draftReturned) queryClient.setQueriesData({ queryKey: campaignKeys.outreachRowsAll(campaignId) }, (data: unknown) => withDraftBack(data, entryId));
    },
    // A first touch's row is patched above, so the rows and counts are re-read only once the projection has caught up.
    onSettled: (cancelled) => {
      if (cancelled?.kind !== 'initial') return invalidate();
      queryClient.invalidateQueries({ queryKey: campaignKeys.outreachThread(entryId) });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: campaignKeys.outreachRowsAll(campaignId) });
        queryClient.invalidateQueries({ queryKey: campaignKeys.outreachCounts(campaignId) });
      }, PROJECTION_CATCH_UP_MS);
    },
  });

  return { resend, sendAnyway, markSent, cancel };
}
